"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = useControllableState;
var _react = require("react");
/* eslint-disable no-undefined */

// Heavily inspired by https://github.com/adobe/react-spectrum
function useControllableState(_ref) {
  let {
    prop: value,
    defaultProp: defaultValue,
    onChange = () => {}
  } = _ref;
  const [stateValue, setStateValue] = (0, _react.useState)(value || defaultValue);
  const ref = (0, _react.useRef)(value !== undefined);
  const wasControlled = ref.current;
  const isControlled = value !== undefined;
  // Internal state reference for useCallback
  const stateRef = (0, _react.useRef)(stateValue);
  if (wasControlled !== isControlled) {
    // eslint-disable-next-line max-len, no-console
    console.warn(`WARN: A component changed from ${wasControlled ? 'controlled' : 'uncontrolled'} to ${isControlled ? 'controlled' : 'uncontrolled'}.`);
  }
  ref.current = isControlled;
  const setValue = (0, _react.useCallback)(function (value) {
    for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      args[_key - 1] = arguments[_key];
    }
    const onChangeCaller = function (value) {
      if (onChange) {
        if (!Object.is(stateRef.current, value)) {
          for (var _len2 = arguments.length, onChangeArgs = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
            onChangeArgs[_key2 - 1] = arguments[_key2];
          }
          onChange(value, ...onChangeArgs);
        }
      }
      if (!isControlled) {
        stateRef.current = value;
      }
    };
    if (typeof value === 'function') {
      // eslint-disable-next-line max-len, no-console
      console.warn('We can not support a function callback. See Github Issues for details https://github.com/adobe/react-spectrum/issues/2320');
      // this supports functional updates https://reactjs.org/docs/hooks-reference.html#functional-updates
      // when someone using useControlledState calls setControlledState(myFunc)
      // this will call our useState setState with a function as well which invokes myFunc and calls onChange with
      // the value from myFunc if we're in an uncontrolled state, then we also return the value of myFunc which to
      // setState looks as though it was just called with myFunc from the beginning otherwise we just return the
      // controlled value, which won't cause a rerender because React knows to bail out when the value is the same
      const updateFunction = function (oldValue) {
        for (var _len3 = arguments.length, functionArgs = new Array(_len3 > 1 ? _len3 - 1 : 0), _key3 = 1; _key3 < _len3; _key3++) {
          functionArgs[_key3 - 1] = arguments[_key3];
        }
        const interceptedValue = value(isControlled ? stateRef.current : oldValue, ...functionArgs);
        onChangeCaller(interceptedValue, ...args);
        if (!isControlled) {
          return interceptedValue;
        }
        return oldValue;
      };
      setStateValue(updateFunction);
    } else {
      if (!isControlled) {
        setStateValue(value);
      }
      onChangeCaller(value, ...args);
    }
  }, [isControlled, onChange]);
  // If a controlled component's value prop changes, we need to update stateRef
  if (isControlled) {
    stateRef.current = value;
  } else {
    value = stateValue;
  }
  return [value, setValue];
}